import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { get, post, put, remove } from 'helpers/apiHelpers';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import SweetAlertConfirmation from 'components/SweetAlertConfirmation/SweetAlertConfirmation';

import CommunicationRulesTable from './CommunicationRulesTable';
import {
  createEmptyRow,
  getDefaultDirection,
  isErrorWithoutResponse,
  ruleToRow,
  rowToPayload,
  rowToUpdatePayload,
  validateRow,
  type CommunicationDictionary,
  type CommunicationRule,
  type CommunicationRuleRow,
} from './communicationRulesHelpers';

const EMPTY_DICTIONARY: CommunicationDictionary = {
  eventTypes: [],
  channels: [],
  eventTypeDefinitions: [],
};

const CommunicationRules = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CommunicationRuleRow[]>([]);
  const [dictionary, setDictionary] =
    useState<CommunicationDictionary>(EMPTY_DICTIONARY);
  const [rowKeyToRemove, setRowKeyToRemove] = useState<string | null>(null);
  const [savingRowKeys, setSavingRowKeys] = useState<string[]>([]);
  const savingRowKeysRef = useRef(new Set<string>());
  const newRowCounter = useRef(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [rulesResponse, dictionaryResponse] = await Promise.all([
          get('communication-rules', { pagination: false }),
          get('communication-dictionaries'),
        ]);

        setRows(
          (rulesResponse['hydra:member'] as CommunicationRule[]).map(ruleToRow)
        );
        setDictionary({
          ...EMPTY_DICTIONARY,
          ...dictionaryResponse['hydra:member'][0],
        });
      } catch (error) {
        if (isErrorWithoutResponse(error)) {
          toast.error(t('communicationRules.loadError'));
        }
      }
    };

    load();
  }, [t]);

  const updateRow = (key: string, changes: Partial<CommunicationRuleRow>) =>
    setRows(prevRows =>
      prevRows.map(row => (row.key === key ? { ...row, ...changes } : row))
    );

  const toggleActive = (key: string) =>
    setRows(prevRows =>
      prevRows.map(row =>
        row.key === key ? { ...row, active: !row.active } : row
      )
    );

  const markRowSaving = (key: string, isSaving: boolean) => {
    if (isSaving) {
      savingRowKeysRef.current.add(key);
    } else {
      savingRowKeysRef.current.delete(key);
    }

    setSavingRowKeys(Array.from(savingRowKeysRef.current));
  };

  const addRow = () => {
    newRowCounter.current += 1;
    setRows(prevRows => [
      ...prevRows,
      createEmptyRow(`new-${newRowCounter.current}`, dictionary.channels),
    ]);
  };

  const changeEventType = (key: string, eventType: string) =>
    updateRow(key, {
      eventType,
      direction: getDefaultDirection(
        dictionary.eventTypeDefinitions,
        eventType
      ),
    });

  const replaceRow = (key: string, saved: CommunicationRule) =>
    setRows(prevRows =>
      prevRows.map(item => (item.key === key ? ruleToRow(saved) : item))
    );

  const saveRow = async (row: CommunicationRuleRow) => {
    if (savingRowKeysRef.current.has(row.key)) {
      return;
    }

    const validationError = row['@id']
      ? null
      : validateRow(row, dictionary.eventTypeDefinitions);

    if (validationError) {
      toast.error(t(validationError.translationKey, validationError.params));

      return;
    }

    markRowSaving(row.key, true);

    try {
      const saved: CommunicationRule = row['@id']
        ? await put(row['@id'], rowToUpdatePayload(row))
        : await post('communication-rules', rowToPayload(row));

      replaceRow(row.key, saved);
      toast.success(t('notify.changesSaved'));
    } catch (error) {
      if (isErrorWithoutResponse(error)) {
        toast.error(t('notify.cannotSave'));
      }
    } finally {
      markRowSaving(row.key, false);
    }
  };

  const confirmRemove = async () => {
    const row = rows.find(item => item.key === rowKeyToRemove);
    setRowKeyToRemove(null);

    if (!row) {
      return;
    }

    if (row['@id']) {
      try {
        await remove(row['@id']);
        toast.success(t('communicationRules.removed'));
      } catch (error) {
        if (isErrorWithoutResponse(error)) {
          toast.error(t('communicationRules.removeError'));
        }

        return;
      }
    }

    setRows(prevRows => prevRows.filter(item => item.key !== row.key));
  };

  return (
    <Card>
      <CardBody>
        {rowKeyToRemove && (
          <SweetAlertConfirmation
            title={t('communicationRules.removeConfirmation')}
            onConfirm={confirmRemove}
            onCancel={() => setRowKeyToRemove(null)}
          />
        )}
        <CommunicationRulesTable
          rows={rows}
          dictionary={dictionary}
          onAddRow={addRow}
          onChangeEventType={changeEventType}
          onChangeRow={updateRow}
          onToggleActive={toggleActive}
          savingRowKeys={savingRowKeys}
          onSaveRow={saveRow}
          onRemoveRow={setRowKeyToRemove}
        />
      </CardBody>
    </Card>
  );
};

export default CommunicationRules;
